import type { IAccountant } from 'types/accountant.types';
import type { IUser } from 'types/users.types';
import { indexedLanguages } from 'utils/languages';
import { isExpert } from 'utils/constants';
import useGetCurrentCustomerData from './useGetCurrentCustomerData';
import useGetExpertLoggedInUserData from './useGetExpertLoggedInUserData';
import { UseQueryResult } from 'react-query';
import { useEffect, useMemo } from 'react';

/**
 * Common logged-in user data - fields that exist in BOTH web and expert
 * No fake fields, only real data
 */
export type LoggedInUserData = {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string | null;
  language: string; // Normalized to 'en', 'fr', 'nl', 'de'
  country: string;
  created?: string;
};

// Transform IUser (web) → LoggedInUserData
const selectFromWebUser = (data: unknown) => {
  if (!data) return data;
  const user = data as IUser;
  return {
    _id: user._id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    phoneNumber: user.phoneNumber || null,
    language: indexedLanguages[user.language] || 'en', // number → string
    country: user.country,
    created: user.created,
  };
};

// Transform IAccountant (expert) → LoggedInUserData
const selectFromAccountant = (data: unknown) => {
  if (!data) return data;
  const accountant = data as IAccountant;
  return {
    _id: accountant._id,
    email: accountant.email,
    firstName: accountant.first_name || '',
    lastName: accountant.last_name || '',
    phoneNumber: accountant.phone_number || null,
    language: accountant.language, // already string
    country: accountant.country,
    created: accountant.created as string,
  };
};

/**
 * UNIVERSAL HOOK - Works for both web and expert
 * Returns only common fields (LoggedInUserData)
 */
const useGetLoggedInUserData = () => {
  const userQuery = useGetCurrentCustomerData();
  const accountantQuery = useGetExpertLoggedInUserData();

  const userData = useMemo(() => {
    return selectFromWebUser(userQuery.data);
  }, [userQuery.data]);

  const mappedUserQuery = {
    ...userQuery,
    data: userData,
  };

  const accountantData = useMemo(() => {
    return selectFromAccountant(accountantQuery.data);
  }, [accountantQuery.data]);

  const mappedAccountantQuery = {
    ...accountantQuery,
    data: accountantData,
  };

  const result = isExpert ? mappedAccountantQuery : mappedUserQuery;

  return result as UseQueryResult<LoggedInUserData, unknown>;
};

export default useGetLoggedInUserData;
